import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../components/ui/Toast';
import { Button, Input, Select, Card, DataTable, Modal } from '../../components/ui';
import { ConfirmModal } from '../../components/ui/Modal';

function TipologieFornituraPage() {
  const { apiRequest } = useAuth();
  const { showToast } = useToast();

  const [tipologie, setTipologie] = useState([]);
  const [macrocategorie, setMacrocategorie] = useState([]);

  const getMacrocategoriaLabel = (value) => {
    const found = macrocategorie.find(m => m.value === String(value));
    return found ? found.label : '-';
  };
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    phc_name: '',
    phc_descrizione: '',
    phc_macrocategoria: '',
    phc_tag: '',
    phc_attiva: true
  });

  // Load tipologie
  useEffect(() => {
    loadTipologie();
  }, []);

  const loadTipologie = async () => {
    setLoading(true);
    try {
      const [data, macrocategorieData] = await Promise.all([
        apiRequest('/tipologie-fornitura'),
        apiRequest('/tipologie-intervento/macrocategorie')
      ]);
      setTipologie(data.value || []);
      setMacrocategorie(macrocategorieData || []);
    } catch (error) {
      console.error('Error loading tipologie:', error);
      showToast('Errore nel caricamento delle tipologie', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setEditingItem(null);
    setFormData({
      phc_name: '',
      phc_descrizione: '',
      phc_macrocategoria: '',
      phc_tag: '',
      phc_attiva: true
    });
    setModalOpen(true);
  };

  const handleOpenEdit = (item) => {
    setEditingItem(item);
    setFormData({
      phc_name: item.nome || item.phc_name || '',
      phc_descrizione: item.descrizione || item.phc_descrizione || '',
      phc_macrocategoria: String(item.macrocategoria || item.phc_macrocategoria || ''),
      phc_tag: item.tag || item.phc_tag || '',
      phc_attiva: item.attiva !== undefined ? item.attiva : (item.phc_attiva !== undefined ? item.phc_attiva : true)
    });
    setModalOpen(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.phc_name.trim()) {
      showToast('Il nome è obbligatorio', 'error');
      return;
    }

    setSaving(true);
    try {
      const dataToSend = {
        ...formData,
        phc_macrocategoria: formData.phc_macrocategoria ? parseInt(formData.phc_macrocategoria) : null
      };

      if (editingItem) {
        await apiRequest(`/tipologie-fornitura/${editingItem.id}`, {
          method: 'PUT',
          body: JSON.stringify(dataToSend)
        });
        showToast('Tipologia aggiornata con successo', 'success');
      } else {
        await apiRequest('/tipologie-fornitura', {
          method: 'POST',
          body: JSON.stringify(dataToSend)
        });
        showToast('Tipologia creata con successo', 'success');
      }

      setModalOpen(false);
      loadTipologie();
    } catch (error) {
      console.error('Error saving tipologia:', error);
      showToast(error.message || 'Errore durante il salvataggio', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;

    try {
      await apiRequest(`/tipologie-fornitura/${itemToDelete.id}`, {
        method: 'DELETE'
      });
      showToast('Tipologia eliminata con successo', 'success');
      setDeleteConfirmOpen(false);
      setItemToDelete(null);
      loadTipologie();
    } catch (error) {
      console.error('Error deleting tipologia:', error);
      showToast(error.message || 'Errore durante l\'eliminazione', 'error');
    }
  };

  const confirmDelete = (item) => {
    setItemToDelete(item);
    setDeleteConfirmOpen(true);
  };

  // Table columns
  const columns = [
    {
      key: 'nome',
      label: 'Nome',
      sortable: true,
      render: (row) => (
        <div>
          <div className="font-medium text-gray-900">{row.nome || row.phc_name}</div>
          {(row.descrizione || row.phc_descrizione) && (
            <div className="text-sm text-gray-500 truncate max-w-xs">
              {row.descrizione || row.phc_descrizione}
            </div>
          )}
        </div>
      )
    },
    {
      key: 'macrocategoria',
      label: 'Macrocategoria',
      sortable: true,
      render: (row) => getMacrocategoriaLabel(row.macrocategoria || row.phc_macrocategoria)
    },
    {
      key: 'tag',
      label: 'Tag',
      render: (row) => {
        const tags = (row.tag || row.phc_tag || '').split(',').filter(t => t.trim());
        return tags.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {tags.slice(0, 3).map((tag, i) => (
              <span key={i} className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded">
                {tag.trim()}
              </span>
            ))}
            {tags.length > 3 && (
              <span className="px-2 py-0.5 text-gray-500 text-xs">+{tags.length - 3}</span>
            )}
          </div>
        ) : '-';
      }
    },
    {
      key: 'attiva',
      label: 'Stato',
      render: (row) => {
        const isActive = row.attiva !== undefined ? row.attiva : row.phc_attiva;
        return (
          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
            isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
          }`}>
            {isActive ? 'Attiva' : 'Inattiva'}
          </span>
        );
      }
    },
    {
      key: 'actions',
      label: 'Azioni',
      render: (row) => (
        <div className="flex gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); handleOpenEdit(row); }}
            className="p-1 text-primary-600 hover:text-primary-800"
            title="Modifica"
          >
            ✏️
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); confirmDelete(row); }}
            className="p-1 text-red-600 hover:text-red-800"
            title="Elimina"
          >
            🗑️
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
            <a href="/admin" className="hover:text-primary-600">Admin</a>
            <span>/</span>
            <span>Tipologie Fornitura</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Tipologie Fornitura</h1>
          <p className="text-gray-500 mt-1">
            Gestisci le categorie di servizi offerti dai fornitori
          </p>
        </div>

        <Button variant="primary" onClick={handleOpenCreate}>
          + Nuova Tipologia
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="p-4">
          <div className="text-2xl font-bold text-gray-900">{tipologie.length}</div>
          <div className="text-sm text-gray-500">Totale Tipologie</div>
        </Card>
        <Card className="p-4">
          <div className="text-2xl font-bold text-green-600">
            {tipologie.filter(t => t.attiva !== false && t.phc_attiva !== false).length}
          </div>
          <div className="text-sm text-gray-500">Attive</div>
        </Card>
        <Card className="p-4">
          <div className="text-2xl font-bold text-gray-400">
            {tipologie.filter(t => t.attiva === false || t.phc_attiva === false).length}
          </div>
          <div className="text-sm text-gray-500">Inattive</div>
        </Card>
      </div>

      {/* Table */}
      <Card className="p-0 overflow-hidden">
        <DataTable
          data={tipologie}
          columns={columns}
          loading={loading}
          searchable
          searchPlaceholder="Cerca tipologia..."
          emptyMessage="Nessuna tipologia fornitura trovata"
          onRowClick={handleOpenEdit}
        />
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingItem ? 'Modifica Tipologia Fornitura' : 'Nuova Tipologia Fornitura'}
        size="lg"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Nome *"
            name="phc_name"
            value={formData.phc_name}
            onChange={handleChange}
            placeholder="es. Manutenzione Impianti Elettrici"
            maxLength={200}
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Descrizione
            </label>
            <textarea
              name="phc_descrizione"
              value={formData.phc_descrizione}
              onChange={handleChange}
              placeholder="Descrizione della tipologia di fornitura..."
              className="form-input w-full h-24"
              maxLength={2000}
            />
          </div>

          <Select
            label="Macrocategoria"
            name="phc_macrocategoria"
            value={formData.phc_macrocategoria}
            onChange={handleChange}
            options={macrocategorie}
            placeholder="Seleziona categoria..."
          />

          <Input
            label="Tag"
            name="phc_tag"
            value={formData.phc_tag}
            onChange={handleChange}
            placeholder="tag1, tag2, tag3"
            helperText="Separare i tag con virgola"
            maxLength={500}
          />

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              name="phc_attiva"
              checked={formData.phc_attiva}
              onChange={handleChange}
              className="h-4 w-4 rounded border-gray-300 text-primary-600"
            />
            <span className="text-gray-700">Tipologia attiva</span>
          </label>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="ghost" onClick={() => setModalOpen(false)}>
              Annulla
            </Button>
            <Button type="submit" variant="primary" disabled={saving}>
              {saving ? 'Salvataggio...' : (editingItem ? 'Aggiorna' : 'Crea')}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmModal
        isOpen={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDelete}
        title="Elimina Tipologia Fornitura"
        message={`Sei sicuro di voler eliminare la tipologia "${itemToDelete?.nome || itemToDelete?.phc_name}"? Questa azione non può essere annullata.`}
        confirmLabel="Elimina"
        variant="danger"
      />
    </div>
  );
}

export default TipologieFornituraPage;
